import type { EventFeedback, EventRegistration, EventResource, EventTask, SchoolEvent } from "../../services/eventService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * School events -- the calendar, who registered and came, what they thought,
 * and the planning tasks behind each one -- and the rules the screens
 * explain. Choices mirror apps/events/models.py; the rules mirror
 * apps/events/views.py and serializers.py.
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const clock = (value?: string | null) => (value ? value.slice(0, 5) : "");
export const labelFrom = (pairs: [string, string][], value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? humanise(value);

export const EVENT_TYPES: [string, string][] = [
  ["academic", "Academic"],
  ["sports", "Sports"],
  ["cultural", "Cultural"],
  ["religious", "Religious"],
  ["social", "Social"],
  ["fundraising", "Fundraising"],
  ["graduation", "Graduation"],
  ["orientation", "Orientation"],
  ["meeting", "Meeting"],
  ["other", "Other"],
];
export const PRIORITIES: [string, string][] = [["low", "Low"], ["medium", "Medium"], ["high", "High"], ["urgent", "Urgent"]];
export const TASK_STATUSES: [string, string][] = [["pending", "To do"], ["in_progress", "Under way"], ["completed", "Done"], ["cancelled", "Dropped"]];
export const RESOURCE_TYPES: [string, string][] = [
  ["equipment", "Equipment"],
  ["venue", "Venue"],
  ["catering", "Catering"],
  ["transportation", "Transport"],
  ["decorations", "Decorations"],
  ["materials", "Materials"],
  ["other", "Other"],
];
export const RESOURCE_STATUSES: [string, string][] = [["required", "Needed"], ["ordered", "Ordered"], ["received", "Received"], ["returned", "Returned"]];
export const RATINGS: [string, string][] = [["5", "Excellent"], ["4", "Good"], ["3", "Average"], ["2", "Poor"], ["1", "Very poor"]];

// --- Events ----------------------------------------------------------------------------------

/** "2026-10-11" or "2026-10-11 – 2026-10-13". */
export const dateRange = (row: Pick<SchoolEvent, "start_date" | "end_date">) =>
  row.end_date && row.end_date !== row.start_date ? `${day(row.start_date)} – ${day(row.end_date)}` : day(row.start_date);

/** "09:00–15:00", "From 09:00", or "" when no time is set. */
export const timeRange = (row: Pick<SchoolEvent, "start_time" | "end_time">) =>
  row.start_time ? (row.end_time ? `${clock(row.start_time)}–${clock(row.end_time)}` : `From ${clock(row.start_time)}`) : "";

export function eventStatus(row: Pick<SchoolEvent, "status" | "start_date" | "end_date">, today: string) {
  switch (row.status) {
    case "draft":
      return { label: "Draft", dot: "bg-gray-400" };
    case "cancelled":
      return { label: "Cancelled", dot: "bg-gray-400" };
    case "postponed":
      return { label: "Postponed", dot: "bg-amber-500" };
    case "completed":
      return { label: "Held", dot: "bg-emerald-500" };
    default:
      if (row.end_date < today) return { label: "Over, register open", dot: "bg-red-500" };
      if (row.start_date <= today) return { label: row.start_date === row.end_date ? "Today" : "On now", dot: "bg-sky-500" };
      return { label: "Coming up", dot: "bg-indigo-500" };
  }
}

/** "12 of 50", or just "12" when there is no limit. */
export const places = (row: Pick<SchoolEvent, "registered_count" | "max_attendees">) =>
  row.max_attendees ? `${row.registered_count} of ${row.max_attendees}` : String(row.registered_count);

export const isFull = (row: Pick<SchoolEvent, "registered_count" | "max_attendees">) => Boolean(row.max_attendees && row.registered_count >= row.max_attendees);

/** Why the signed-in person cannot register, or null when they can. Mirrors EventRegistrationSerializer.validate_event. */
export function registrationBlock(row: Pick<SchoolEvent, "status" | "start_date" | "registration_deadline" | "registered_count" | "max_attendees" | "my_registration" | "registration_required">, today: string): string | null {
  if (row.my_registration) return "You are registered.";
  if (!row.registration_required) return "No need to register; just come.";
  if (row.status !== "published") return "Registration opens once the event is published.";
  if (row.start_date < today) return "The event has already started.";
  if (row.registration_deadline && row.registration_deadline < today) return "Registration has closed.";
  if (isFull(row)) return "The event is full.";
  return null;
}

export const eventConfig = (today: string, canManage: boolean): RegisterConfig<SchoolEvent> => ({
  search: (row) => [row.name, row.category_name, row.venue, row.organizer_name, labelFrom(EVENT_TYPES, row.event_type)].join(" "),
  views: [
    { key: "upcoming", label: "Coming up", match: (row) => (row.status === "published" || row.status === "postponed") && row.end_date >= today },
    ...(canManage
      ? [
          { key: "drafts", label: "Drafts", match: (row: SchoolEvent) => row.status === "draft" },
          { key: "close", label: "Register to close", match: (row: SchoolEvent) => row.status === "published" && row.end_date < today },
        ]
      : [{ key: "mine", label: "I'm registered", match: (row: SchoolEvent) => Boolean(row.my_registration) }]),
    { key: "past", label: "Past", match: (row) => row.status === "completed" || (row.status === "published" && row.end_date < today) },
    { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Date", value: (row) => row.start_date },
  category: { label: "Category", value: (row) => row.category_name },
  sorts: [
    { key: "when", label: "Soonest first", compare: byText<SchoolEvent>((row) => `${row.start_date}${row.start_time ?? ""}`) },
    { key: "-when", label: "Latest first", compare: reversed(byText<SchoolEvent>((row) => `${row.start_date}${row.start_time ?? ""}`)) },
    { key: "name", label: "Name (A–Z)", compare: byText<SchoolEvent>((row) => row.name) },
    { key: "-places", label: "Most registered", compare: reversed(byNumber<SchoolEvent>((row) => row.registered_count)) },
  ],
});

// --- Registrations ---------------------------------------------------------------------------

export function registrationStatus(row: Pick<EventRegistration, "status">) {
  switch (row.status) {
    case "attended":
      return { label: "Came", dot: "bg-emerald-500" };
    case "no_show":
      return { label: "Did not come", dot: "bg-red-500" };
    case "cancelled":
      return { label: "Withdrew", dot: "bg-gray-400" };
    default:
      return { label: "Registered", dot: "bg-indigo-500" };
  }
}

export const isWaiting = (row: Pick<EventRegistration, "status">) => row.status === "registered" || row.status === "confirmed";

/** Mirrors EventFeedbackSerializer.validate: registered and not a no-show, once the event has taken place. */
export const canGiveFeedback = (row: Pick<EventRegistration, "status" | "event_status" | "event_start_date" | "has_feedback">, today: string) =>
  !row.has_feedback && ["registered", "confirmed", "attended"].includes(row.status) && !["draft", "cancelled"].includes(row.event_status) && row.event_start_date <= today;

/** Withdrawing is open until the event starts, unless feedback was already given. */
export const canWithdraw = (row: Pick<EventRegistration, "status" | "event_start_date"> & { has_feedback?: boolean }, today: string) => isWaiting(row) && row.event_start_date >= today && !row.has_feedback;

export const registrationConfig = (today: string, canManage: boolean): RegisterConfig<EventRegistration> => ({
  search: (row) => [row.user_name, row.event_name, humanise(row.user_type), row.special_requests, row.dietary_requirements].join(" "),
  views: canManage
    ? [
        { key: "waiting", label: "Registered", match: isWaiting },
        { key: "attended", label: "Came", match: (row) => row.status === "attended" },
        { key: "no_show", label: "Did not come", match: (row) => row.status === "no_show" },
        { key: "cancelled", label: "Withdrew", match: (row) => row.status === "cancelled" },
        { key: "all", label: "All" },
      ]
    : [
        { key: "upcoming", label: "Coming up", match: (row) => isWaiting(row) && row.event_end_date >= today && row.event_status !== "cancelled" },
        { key: "feedback", label: "Feedback to give", match: (row) => canGiveFeedback(row, today) },
        { key: "past", label: "Past", match: (row) => row.event_end_date < today || row.event_status === "completed" },
        { key: "all", label: "All" },
      ],
  date: { label: "Event date", value: (row) => row.event_start_date },
  category: { label: "Event", value: (row) => row.event_name },
  sorts: [
    { key: "-when", label: "Latest event first", compare: reversed(byText<EventRegistration>((row) => row.event_start_date)) },
    { key: "when", label: "Earliest event first", compare: byText<EventRegistration>((row) => row.event_start_date) },
    { key: "name", label: "Name (A–Z)", compare: byText<EventRegistration>((row) => row.user_name) },
  ],
});

/** The guest list in check-in order: still to arrive, then arrived, then the rest. */
export function guestOrder(rows: EventRegistration[]) {
  const rank: Record<string, number> = { registered: 0, confirmed: 0, attended: 1, no_show: 2, cancelled: 3 };
  return [...rows].sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9) || a.user_name.localeCompare(b.user_name));
}

// --- Feedback --------------------------------------------------------------------------------

export const stars = (rating: number) => "★".repeat(rating) + "☆".repeat(Math.max(0, 5 - rating));

export function ratingSummary(rows: Pick<EventFeedback, "overall_rating" | "would_recommend" | "would_attend_again">[]) {
  const counts = [5, 4, 3, 2, 1].map((rating) => ({ rating, count: rows.filter((row) => row.overall_rating === rating).length }));
  const share = (test: (row: (typeof rows)[number]) => boolean) => (rows.length ? Math.round((rows.filter(test).length / rows.length) * 100) : 0);
  return {
    count: rows.length,
    average: rows.length ? rows.reduce((sum, row) => sum + row.overall_rating, 0) / rows.length : null,
    counts,
    recommend: share((row) => row.would_recommend),
    again: share((row) => row.would_attend_again),
  };
}

export const feedbackConfig: RegisterConfig<EventFeedback> = {
  search: (row) => [row.event_name, row.user_name, row.comments, row.suggestions].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "low", label: "Poor (1–2)", match: (row) => row.overall_rating <= 2 },
    { key: "high", label: "Good (4–5)", match: (row) => row.overall_rating >= 4 },
    { key: "suggestions", label: "With suggestions", match: (row) => Boolean(row.suggestions?.trim()) },
  ],
  date: { label: "Event date", value: (row) => row.event_start_date },
  category: { label: "Event", value: (row) => row.event_name },
  sorts: [
    { key: "-when", label: "Newest first", compare: reversed(byText<EventFeedback>((row) => row.created_at)) },
    { key: "rating", label: "Lowest rating", compare: byNumber<EventFeedback>((row) => row.overall_rating) },
    { key: "-rating", label: "Highest rating", compare: reversed(byNumber<EventFeedback>((row) => row.overall_rating)) },
  ],
};

// --- Planning --------------------------------------------------------------------------------

export const isOpenTask = (row: Pick<EventTask, "status">) => row.status === "pending" || row.status === "in_progress";

export function taskStatus(row: Pick<EventTask, "status" | "due_date">, today: string) {
  if (row.status === "completed") return { label: "Done", dot: "bg-emerald-500" };
  if (row.status === "cancelled") return { label: "Dropped", dot: "bg-gray-400" };
  if (row.due_date && row.due_date < today) return { label: "Overdue", dot: "bg-red-500" };
  if (row.due_date === today) return { label: "Due today", dot: "bg-amber-500" };
  return row.status === "in_progress" ? { label: "Under way", dot: "bg-sky-500" } : { label: "To do", dot: "bg-indigo-500" };
}

const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

export const taskConfig = (today: string, me: number | undefined): RegisterConfig<EventTask> => ({
  search: (row) => [row.title, row.event_name, row.assigned_to_name, row.description].join(" "),
  views: [
    { key: "open", label: "Open", match: isOpenTask },
    { key: "mine", label: "Mine", match: (row) => isOpenTask(row) && row.assigned_to === me },
    { key: "overdue", label: "Overdue", match: (row) => isOpenTask(row) && Boolean(row.due_date && row.due_date < today) },
    { key: "done", label: "Done", match: (row) => row.status === "completed" },
    { key: "all", label: "All" },
  ],
  date: { label: "Due", value: (row) => row.due_date },
  category: { label: "Event", value: (row) => row.event_name },
  sorts: [
    // Tasks with no due date go last.
    { key: "due", label: "Due soonest", compare: byText<EventTask>((row) => row.due_date ?? "9999") },
    { key: "priority", label: "Most urgent", compare: (a, b) => (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) || (a.due_date ?? "9999").localeCompare(b.due_date ?? "9999") },
    { key: "event", label: "By event", compare: byText<EventTask>((row) => `${row.event_start_date} ${row.event_name}`) },
  ],
});

/** What the resources for an event cost, leaving out any without a price. */
export const resourceCost = (rows: Pick<EventResource, "cost">[]) => rows.reduce((sum, row) => sum + (row.cost ? Number(row.cost) : 0), 0);

export const money = (value: number) => `UGX ${Math.round(value).toLocaleString()}`;
